<script lang="ts">
  import type {
    ClientRatioCategory,
    FirmPatchRequest,
  } from "@shared/types/firm";
  import type { Firm } from "@domain/firm/model";
  import { isLocked } from "@domain/firm/locked-columns";
  import { patchFirm } from "@entities/firm/store.svelte";
  import InlineField from "@shared/ui/InlineField.svelte";
  import InlineMultiSelectField from "@shared/ui/InlineMultiSelectField.svelte";
  import BulkSalesChannelEditor from "@features/bulk-sales-channel-edit/BulkSalesChannelEditor.svelte";
  import { bulkSalesFlagsOf } from "@domain/bulk-sales/labels";
  import type { BulkSalesFlags } from "@domain/bulk-sales/model";
  import SetRepresentativesModal from "@features/lawyer-set-representative/SetRepresentativesModal.svelte";
  import type { Lawyer } from "@domain/lawyer/model";
  // ソリューション利用パネルは firm-summary / lawyer-detail 両方の widget から所属
  // 事務所軸で再利用するため、widget ↔ widget の例外として明示的に許可する。
  // eslint-disable-next-line import/no-restricted-paths
  import CompetitorServicesPanel from "@widgets/competitor-services-panel/CompetitorServicesPanel.svelte";
  import {
    BAR_ASSOCIATION_DISPLAY_OPTIONS,
    CLIENT_RATIO_CATEGORY_OPTIONS,
    LEVEL_LMH_OPTIONS,
    ORGANIZATION_TYPE_OPTIONS,
    FIRM_PRACTICE_AREA_OPTIONS,
    PREFECTURE_OPTIONS,
    formatBarAssociationLabel,
  } from "@shared/constants/choices";
  import {
    findMunicipalityCode,
    municipalitiesForPrefecture,
    municipalityForCode,
    wardsForMunicipality,
  } from "@shared/constants/address-master";

  /**
   * 法律事務所 左カラム v2 仕様
   * (docs/implementation/customer_profile_left_column_v2_spec.md /
   *  docs/implementation/20260603-firm-detail-profile-field-simplification.md):
   *
   *   基本情報 / 連絡先 / 属性 / ソリューション利用 / 営業対象外設定
   *
   * 正式名称はヘッダーの主タイトルと重複するため左カラムから外す
   * (official_name の保存値・必須性・API は維持)。Board顧客番号・担当者も
   * 左カラムの通常項目から外す (担当者は FirmDetailPage ヘッダー右上へ移動)。
   * 住所は 都道府県 / 市区町村 / 番地以下 を連結した「住所」1 行で表示し、
   * 編集も 3 入力をまとめて行う (保存値は 3 分割のまま維持)。
   * 上記以外の通称・公式SNS・売上規模 等も通常UIから外す
   * (DB / migration / seed / API は維持)。
   */

  interface Props {
    firm: Firm;
    canEdit: boolean;
    /// 代表弁護士 (0..N) は所属弁護士から複数選択する
    /// (spec 20260612-multiple-firm-representative-lawyers)。保存は generic PATCH
    /// ではなく専用 API (PUT /representatives) を親 (FirmDetailPage) から呼ぶ。
    representativeCandidates?: Lawyer[];
    onRepresentativesChange?: (lawyerIds: string[]) => void | Promise<void>;
  }

  let {
    firm,
    canEdit,
    representativeCandidates = [],
    onRepresentativesChange,
  }: Props = $props();

  type EditableTextKey =
    | "bar_association"
    | "organization_type"
    | "phone"
    | "phone_note"
    | "email"
    | "fax"
    | "website_url"
    | "client_ratio_category"
    | "it_literacy"
    | "competitor_reason";
  type GeneralEditableTextKey = Exclude<
    EditableTextKey,
    "client_ratio_category"
  >;

  type EditableNumberKey = "staff_count";

  type EditableDateKey = "established_date";

  type EditableKey = EditableTextKey | EditableNumberKey | EditableDateKey;

  interface SelectOption {
    value: string;
    label: string;
  }

  interface FieldSpec {
    key: EditableKey;
    label: string;
    type?: "text" | "number" | "date" | "select";
    options?: SelectOption[];
    /// クリック前に種類が分かる opt-in 表示 (PR-A #560)。 既定は従来挙動。
    affordance?: "text" | "select" | "select-compact";
    /// false で空値への解除を不可にする (既定 true)。 save() が空を弾く項目向け。
    allowEmpty?: boolean;
  }

  // 基本情報セクションのうち、 InlineField で編集する行 (`代表弁護士` 行は
  // 別途 read-only で描画する)。 弁護士会 / 組織形態 は旧 UI で dropdown
  // だった項目なので、 編集モードも select で揃える。
  const basicFields: FieldSpec[] = [
    {
      key: "bar_association",
      label: "弁護士会",
      type: "select",
      options: [...BAR_ASSOCIATION_DISPLAY_OPTIONS],
      // 候補 52 のため large select。 chevron / 未選択 で選択式と分かるが
      // 候補は常時展開しない。
      affordance: "select",
    },
    {
      key: "organization_type",
      label: "組織形態",
      type: "select",
      options: [...ORGANIZATION_TYPE_OPTIONS],
      // 候補 3 (弁護士法人/個人事務所/共同事務所) で横幅も収まるため compact。
      affordance: "select-compact",
    },
  ];

  // 連絡先セクションは 郵便番号 → 住所 → 代表電話 → 代表メール → 公式サイトURL の
  // 順でテンプレートに直接描画する。住所は構造化列を連結表示し、編集はマスター選択と
  // 番地以下を住所オブジェクトとしてまとめて保存する。

  function isClientRatioCategory(value: string): value is ClientRatioCategory {
    return CLIENT_RATIO_CATEGORY_OPTIONS.some((opt) => opt.value === value);
  }

  // 属性セクション。 クライアント比率 / IT リテラシーは select で揃える。
  // 主要取扱分野は別途 InlineMultiSelectField で表示する。
  const attributeFields: FieldSpec[] = [
    {
      key: "client_ratio_category",
      label: "クライアント比率",
      type: "select",
      options: [...CLIENT_RATIO_CATEGORY_OPTIONS],
      // 候補 3。 クリック前に選択肢が分かるよう compact。
      affordance: "select-compact",
      // save() が空値を弾く (isClientRatioCategory) ため解除不可。 `未設定` chip を出さない。
      allowEmpty: false,
    },
    {
      key: "it_literacy",
      label: "ITリテラシー",
      type: "select",
      options: [...LEVEL_LMH_OPTIONS],
      // 候補 3 (高/中/低)。 compact。
      affordance: "select-compact",
    },
  ];

  async function save(field: EditableKey, next: string) {
    const body: FirmPatchRequest = {};
    if (field === "staff_count") {
      const num = Number(next);
      if (Number.isNaN(num)) {
        throw new Error("数値を入力してください");
      }
      body[field] = num;
    } else if (field === "client_ratio_category") {
      if (!isClientRatioCategory(next)) {
        throw new Error("クライアント比率を選択してください");
      }
      body.client_ratio_category = next;
    } else {
      body[field as GeneralEditableTextKey] = next;
    }
    await patchFirm(firm.id, body);
  }

  /// `practice_areas` のような JSONB array カラム用。
  /// 空配列は `null` に倒して 「未設定」 として保存する。
  async function saveMultiArray(field: "practice_areas", next: string[]) {
    const body: FirmPatchRequest = {};
    body[field] = next.length > 0 ? next : null;
    await patchFirm(firm.id, body);
  }

  /// 事務所の一括営業チャネル除外 (spec 20260716 Scope D)。旧「営業NG」チェックの置換。
  /// 単一チェックは 1 boolean、一括操作は 5 boolean をまとめて PATCH する (専用フィールド・
  /// 理由・日付なし)。競合フラグ (is_competitor) とは独立した概念で自動反映しない。
  const bulkSalesFlags = $derived(bulkSalesFlagsOf(firm));
  async function saveBulkSalesFlags(patch: Partial<BulkSalesFlags>): Promise<void> {
    await patchFirm(firm.id, patch);
  }

  /// 表示は構造化住所を連結し、編集は住所マスターからの選択 + 番地以下で行う。
  /// 東京都特別区は city、政令市行政区は ward、郡は county に保存する。
  const addressDisplay = $derived(
    [firm.prefecture, firm.county, firm.city, firm.ward, firm.address_line]
      .map((v) => (v ?? "").trim())
      .filter((v) => v.length > 0)
      .join(""),
  );
  const addressLocked = $derived(
    isLocked("postal_code") ||
      isLocked("prefecture") ||
      isLocked("county") ||
      isLocked("city") ||
      isLocked("ward") ||
      isLocked("address_line"),
  );
  let addressEditing = $state(false);
  let addressSaving = $state(false);
  let addressError = $state<string | null>(null);
  let addrPrefecture = $state("");
  let addrMunicipalityCode = $state("");
  let addrWard = $state("");
  let addrLine = $state("");
  const selectedAddressMunicipality = $derived(
    municipalityForCode(addrMunicipalityCode),
  );
  const currentAddressMasterMismatch = $derived(
    Boolean(
      firm.prefecture?.trim() || firm.county?.trim() || firm.city?.trim(),
    ) && !findMunicipalityCode(firm.prefecture, firm.county, firm.city),
  );

  function startAddressEdit(): void {
    if (!canEdit || addressLocked || addressSaving) return;
    addrPrefecture = firm.prefecture ?? "";
    addrMunicipalityCode =
      findMunicipalityCode(firm.prefecture, firm.county, firm.city) ?? "";
    addrWard = firm.ward ?? "";
    addrLine = firm.address_line ?? "";
    addressError = null;
    addressEditing = true;
  }

  function cancelAddressEdit(): void {
    addressEditing = false;
    addressError = null;
  }

  function selectAddressPrefecture(value: string): void {
    addrPrefecture = value;
    addrMunicipalityCode = "";
    addrWard = "";
  }

  function selectAddressMunicipality(value: string): void {
    addrMunicipalityCode = value;
    addrWard = "";
  }

  function currentAddress(postalCode: string | null) {
    return {
      postal_code: postalCode,
      prefecture: firm.prefecture,
      county: firm.county,
      city: firm.city,
      ward: firm.ward,
      address_line: firm.address_line,
    };
  }

  async function savePostalCode(next: string): Promise<void> {
    const hasMunicipality =
      Boolean(firm.prefecture?.trim()) ||
      Boolean(firm.county?.trim()) ||
      Boolean(firm.city?.trim()) ||
      Boolean(firm.ward?.trim());
    const municipalityCode = findMunicipalityCode(
      firm.prefecture,
      firm.county,
      firm.city,
    );
    const municipality = municipalityForCode(municipalityCode);
    const hasValidWard =
      !municipality?.requiresWard ||
      wardsForMunicipality(municipalityCode).some(
        (option) => option.value === firm.ward?.trim(),
      );
    if ((hasMunicipality && !municipalityCode) || !hasValidWard) {
      throw new Error(
        "現在の住所が住所マスターと一致しません。先に住所欄から住所を再選択してください",
      );
    }
    await patchFirm(firm.id, {
      address: currentAddress(next.trim() || null),
    });
  }

  async function saveAddress(): Promise<void> {
    if (addressSaving) return;
    if (addrPrefecture && !selectedAddressMunicipality) {
      addressError = "市区町村を選択してください";
      return;
    }
    if (selectedAddressMunicipality?.requiresWard && !addrWard) {
      addressError = "区を選択してください";
      return;
    }
    addressSaving = true;
    addressError = null;
    try {
      // address 指定時は6項目を full replacement し、null はDB NULLへクリアする。
      await patchFirm(firm.id, {
        address: {
          postal_code: firm.postal_code ?? null,
          prefecture: addrPrefecture.trim() || null,
          county: selectedAddressMunicipality?.county ?? null,
          city: selectedAddressMunicipality?.city ?? null,
          ward: addrWard.trim() || null,
          address_line: addrLine.trim() || null,
        },
      });
      addressEditing = false;
    } catch (err) {
      addressError = err instanceof Error ? err.message : String(err);
    } finally {
      addressSaving = false;
    }
  }

  /// Enter で保存、Esc で取消。3 入力共通の keydown ハンドラ。
  function onAddressKey(ev: KeyboardEvent): void {
    if (ev.key === "Enter") {
      ev.preventDefault();
      void saveAddress();
    } else if (ev.key === "Escape") {
      ev.preventDefault();
      cancelAddressEdit();
    }
  }

  let isCompetitorSaving = $state(false);
  async function saveIsCompetitor(next: boolean) {
    if (isCompetitorSaving) return;
    isCompetitorSaving = true;
    try {
      await patchFirm(firm.id, { is_competitor: next });
    } finally {
      isCompetitorSaving = false;
    }
  }

  function valueOf(field: EditableKey): string | number | null {
    const v = firm[field];
    if (typeof v === "string" || typeof v === "number" || v === null) return v;
    return null;
  }

  function locked(field: EditableKey): boolean {
    return isLocked(field);
  }

  function asStringArray(value: unknown): string[] | null {
    if (!Array.isArray(value)) return null;
    const out = value.filter(
      (v): v is string => typeof v === "string" && v.length > 0,
    );
    return out.length > 0 ? out : null;
  }

  /// 代表弁護士 (0..N)。 backend embed (`representative_lawyers`, 氏名順) を
  /// タグ列挙で表示する。 0 名は `—`。 編集は複数選択モーダル (全解除可)。
  const representativeLawyers = $derived(firm.representative_lawyers ?? []);
  const representativeLawyerIds = $derived(firm.representative_lawyer_ids);
  /// モーダルへ渡す現代表。 候補 (全所属弁護士) の取得失敗・打ち切り等で
  /// 候補リストに現れない代表も embed 由来で行表示できるようにする防御。
  /// embed 未取得時は id で補完 (通常 detail では常に embed される)。
  const currentRepresentativeRows = $derived(
    representativeLawyers.length > 0
      ? representativeLawyers.map((rep) => ({
          id: rep.id as string,
          full_name: rep.full_name,
          bar_association: rep.bar_association,
        }))
      : representativeLawyerIds.map((id) => ({
          id: id as string,
          full_name: id as string,
          bar_association: null,
        })),
  );
  let representativesModalOpen = $state(false);

  async function handleRepresentativesSubmit(
    lawyerIds: string[],
  ): Promise<void> {
    if (!onRepresentativesChange) return;
    await onRepresentativesChange(lawyerIds);
    representativesModalOpen = false;
  }

  const practiceAreas = $derived(asStringArray(firm.practice_areas));
</script>

<div class="summary" data-testid="firm-summary-v2">
  <!-- 1. 基本情報 -->
  <section data-testid="firm-summary-basic">
    <h3>基本情報</h3>
    <div class="grid">
      <div
        class="readonly-cell"
        data-testid="firm-summary-representative-lawyer"
      >
        <span class="readonly-label">代表弁護士</span>
        <span class="readonly-value">
          {#if representativeLawyers.length > 0}
            <span class="representative-tags">
              {#each representativeLawyers as rep (rep.id)}
                <span class="representative-tag">
                  <a href={`/lawyers/${rep.id}`}>{rep.full_name.trim()}</a>
                  {#if rep.bar_association}
                    <span class="muted small"
                      >（{formatBarAssociationLabel(
                        rep.bar_association,
                      )}）</span
                    >
                  {/if}
                </span>
              {/each}
            </span>
          {:else if representativeLawyerIds.length > 0}
            <!-- embed 未取得時の ID フォールバック (通常は detail で常に embed される) -->
            <span class="representative-tags">
              {#each representativeLawyerIds as repId (repId)}
                <span class="representative-tag">
                  <a href={`/lawyers/${repId}`}>{repId}</a>
                </span>
              {/each}
            </span>
          {:else}
            <span class="muted">—</span>
          {/if}
          {#if canEdit && onRepresentativesChange}
            <button
              type="button"
              class="representative-edit-btn"
              data-testid="firm-summary-representative-edit"
              onclick={() => (representativesModalOpen = true)}
            >
              変更
            </button>
          {/if}
        </span>
      </div>
      {#each basicFields as field (field.key)}
        <InlineField
          label={field.label}
          value={valueOf(field.key)}
          type={field.type ?? "text"}
          options={field.options ?? []}
          affordance={field.affordance ?? "default"}
          locked={locked(field.key)}
          disabled={!canEdit}
          onSave={(next) => save(field.key, next)}
        />
      {/each}
      <!-- 所属弁護士数 (catalog No.33) は所属弁護士から導出される派生値のため、
           編集不可の read-only 表示にする (サーバが COUNT で算出)。
           業務要望により 事務員数 の上に表示する (表示順入れ替え)。 -->
      <div class="readonly-cell" data-testid="firm-summary-lawyer-count">
        <span class="readonly-label">所属弁護士数</span>
        <span class="readonly-value">
          {#if firm.lawyer_count != null}
            {firm.lawyer_count}
          {:else}<span class="muted">—</span>{/if}
        </span>
      </div>
      <!-- 事務員数 (staff_count) は編集可能。 所属弁護士数 の下に表示する。 -->
      <InlineField
        label="事務員数"
        value={valueOf("staff_count")}
        type="number"
        affordance="text"
        placeholder="人数を入力"
        locked={locked("staff_count")}
        disabled={!canEdit}
        onSave={(next) => save("staff_count", next)}
      />
    </div>
  </section>

  <!-- 2. 連絡先 -->
  <section data-testid="firm-summary-contact">
    <h3>連絡先</h3>
    <div class="grid">
      <InlineField
        label="郵便番号"
        value={firm.postal_code}
        type="text"
        affordance="text"
        placeholder="郵便番号を入力"
        locked={addressLocked}
        disabled={!canEdit}
        onSave={savePostalCode}
      />
      <!-- 住所: 都道府県 / 郡 / 市区町村 / 政令市行政区 / 番地以下を連結表示。
           編集時の自治体と行政区は承認済み住所マスターから選択する。 -->
      <div class="address-field" data-testid="firm-summary-address">
        <span class="address-label">住所</span>
        <span class="address-value">
          {#if !canEdit || addressLocked}
            {#if addressDisplay}
              <span>{addressDisplay}</span>
            {:else}
              <span class="muted">-</span>
            {/if}
          {:else if addressEditing}
            <span class="address-edit">
              <label class="address-input">
                <span class="address-input-label">都道府県</span>
                <select
                  value={addrPrefecture}
                  disabled={addressSaving}
                  onchange={(ev) =>
                    selectAddressPrefecture(
                      (ev.currentTarget as HTMLSelectElement).value,
                    )}
                  onkeydown={onAddressKey}
                >
                  <option value="">(未設定)</option>
                  {#each PREFECTURE_OPTIONS as option (option.value)}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </label>
              <label class="address-input">
                <span class="address-input-label">市区町村</span>
                <select
                  value={addrMunicipalityCode}
                  disabled={addressSaving || !addrPrefecture}
                  required={addrPrefecture.length > 0}
                  onchange={(ev) =>
                    selectAddressMunicipality(
                      (ev.currentTarget as HTMLSelectElement).value,
                    )}
                  onkeydown={onAddressKey}
                >
                  <option value="">選択してください</option>
                  {#each municipalitiesForPrefecture(addrPrefecture) as option (option.code)}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </label>
              {#if selectedAddressMunicipality?.requiresWard}
                <label class="address-input">
                  <span class="address-input-label">区</span>
                  <select
                    bind:value={addrWard}
                    disabled={addressSaving}
                    required
                    onkeydown={onAddressKey}
                  >
                    <option value="">選択してください</option>
                    {#each wardsForMunicipality(addrMunicipalityCode) as option (option.code)}
                      <option value={option.value}>{option.label}</option>
                    {/each}
                  </select>
                </label>
              {/if}
              {#if currentAddressMasterMismatch && addrPrefecture === (firm.prefecture ?? "") && !addrMunicipalityCode}
                <span class="small error-msg"
                  >現在の住所はマスター未一致です。保存する場合は市区町村を再選択してください。</span
                >
              {/if}
              <label class="address-input">
                <span class="address-input-label">番地以下</span>
                <input
                  type="text"
                  bind:value={addrLine}
                  disabled={addressSaving}
                  placeholder="番地以下"
                  onkeydown={onAddressKey}
                />
              </label>
              <span class="address-actions">
                <button
                  type="button"
                  class="addr-btn addr-save"
                  onclick={saveAddress}
                  disabled={addressSaving ||
                    (addrPrefecture.length > 0 && !addrMunicipalityCode) ||
                    (selectedAddressMunicipality?.requiresWard === true &&
                      !addrWard)}>保存</button
                >
                <button
                  type="button"
                  class="addr-btn"
                  onclick={cancelAddressEdit}
                  disabled={addressSaving}>キャンセル</button
                >
                {#if addressSaving}<span class="muted small">保存中…</span>{/if}
              </span>
              {#if addressError}<span class="small error-msg"
                  >{addressError}</span
                >{/if}
            </span>
          {:else}
            <button
              type="button"
              class="address-display"
              title="クリックして編集"
              onclick={startAddressEdit}
            >
              {#if addressDisplay}
                {addressDisplay}
              {:else}
                <span class="address-placeholder">住所を入力</span>
              {/if}
            </button>
          {/if}
        </span>
      </div>
      <InlineField
        label="代表電話"
        value={valueOf("phone")}
        type="text"
        affordance="text"
        placeholder="電話番号を入力"
        locked={locked("phone")}
        disabled={!canEdit}
        onSave={(next) => save("phone", next)}
      />
      <InlineField
        label="電話メモ"
        value={valueOf("phone_note")}
        type="text"
        affordance="text"
        placeholder="内線・複数番号など"
        locked={locked("phone_note")}
        disabled={!canEdit}
        onSave={(next) => save("phone_note", next)}
      />
      <InlineField
        label="代表メール"
        value={valueOf("email")}
        type="text"
        affordance="text"
        placeholder="メールを入力"
        locked={locked("email")}
        disabled={!canEdit}
        onSave={(next) => save("email", next)}
      />
      <!-- 代表メール直下・公式サイトURL 直上 (spec 20260716 Scope A の表示順)。 -->
      <InlineField
        label="FAX番号"
        value={valueOf("fax")}
        type="text"
        affordance="text"
        placeholder="FAX番号を入力"
        locked={locked("fax")}
        disabled={!canEdit}
        onSave={(next) => save("fax", next)}
      />
      <InlineField
        label="公式サイトURL"
        value={valueOf("website_url")}
        type="text"
        affordance="text"
        placeholder="https://..."
        locked={locked("website_url")}
        disabled={!canEdit}
        onSave={(next) => save("website_url", next)}
      />
    </div>
  </section>

  <!-- 3. 属性 -->
  <section data-testid="firm-summary-attributes">
    <h3>属性</h3>
    <div class="grid">
      <InlineMultiSelectField
        label="主要取扱分野"
        value={practiceAreas}
        options={[...FIRM_PRACTICE_AREA_OPTIONS]}
        affordance="large"
        locked={isLocked("practice_areas")}
        disabled={!canEdit}
        onSave={(next) => saveMultiArray("practice_areas", next)}
      />
      {#each attributeFields as field (field.key)}
        <InlineField
          label={field.label}
          value={valueOf(field.key)}
          type={field.type ?? "text"}
          options={field.options ?? []}
          affordance={field.affordance ?? "default"}
          allowEmpty={field.allowEmpty ?? true}
          locked={locked(field.key)}
          disabled={!canEdit}
          onSave={(next) => save(field.key, next)}
        />
      {/each}
    </div>
  </section>

  <!-- 4. 利用ソリューションパネル。
       customer_competitor_services テーブルの顧客 x 他社ソリューション利用を CRUD し、
       product_competitor_services 由来の競合関係で表示分類する。 事務所単位
       (firm_id) で集約し、 営業NG / 競合(事務所) フラグとは混ぜない。 -->
  <section
    id="competitor-solution-usage"
    data-testid="firm-summary-competitor-services"
  >
    <CompetitorServicesPanel
      target={{ kind: "firm", firmId: firm.id }}
      {canEdit}
    />
  </section>

  <!--
    5. 営業対象外設定 (spec 20260716「法律事務所詳細」)。 競合 (is_competitor) と
    一括営業チャネル除外 (5 boolean) は別概念として独立編集する。 旧「営業NG」チェックは
    表示せず 5 チャネルの一括操作へ置換。 旧営業NG理由は値があるときだけ read-only。
    競合理由は競合 ON 時だけ表示し、 OFF 時にも保存済みテキストは削除しない。
  -->
  <section
    class:exclusion-active={firm.bulk_sales_decision.state !== "target" ||
      firm.is_competitor}
    data-testid="firm-summary-sales-exclusion"
  >
    <h3>営業対象外設定</h3>
    <dl class="kv">
      <div>
        <dt>競合</dt>
        <dd>
          <label class="exclusion-toggle">
            <input
              type="checkbox"
              checked={firm.is_competitor}
              disabled={!canEdit || isCompetitorSaving}
              onchange={(ev) => {
                void saveIsCompetitor(
                  (ev.currentTarget as HTMLInputElement).checked,
                );
              }}
            />
            <span class="checkbox-label">競合</span>
            {#if isCompetitorSaving}<span class="muted small">保存中…</span
              >{/if}
          </label>
        </dd>
      </div>
      {#if firm.is_competitor}
        <div>
          <dt>競合理由</dt>
          <dd>
            <InlineField
              label=""
              value={firm.competitor_reason ?? ""}
              type="text"
              affordance="text"
              placeholder="競合と判断した理由を記入"
              locked={isLocked("competitor_reason")}
              disabled={!canEdit}
              onSave={(next) => save("competitor_reason", next)}
            />
          </dd>
        </div>
      {/if}
      <div>
        <dt>営業対象外チャネル</dt>
        <dd>
          <BulkSalesChannelEditor
            flags={bulkSalesFlags}
            label="一括営業で使用しないチャネル"
            disabled={!canEdit}
            onSave={saveBulkSalesFlags}
          />
        </dd>
      </div>
      {#if firm.sales_exclusion_reason?.trim()}
        <div>
          <dt>旧営業NG理由</dt>
          <dd>
            <span class="legacy-reason" data-testid="firm-legacy-sales-exclusion-reason"
              >{firm.sales_exclusion_reason}</span
            >
          </dd>
        </div>
      {/if}
    </dl>
  </section>
</div>

<SetRepresentativesModal
  open={representativesModalOpen}
  candidates={representativeCandidates}
  current={currentRepresentativeRows}
  onClose={() => (representativesModalOpen = false)}
  onSubmit={handleRepresentativesSubmit}
/>

<style>
  .summary {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
  }
  h3 {
    margin: 0 0 12px;
    font-size: 0.875rem;
    color: #374151;
  }
  /* 旧 FirmSummary の table.kv (key-value 縦テーブル) と同じ見た目に揃える。
     各セルは「ラベル列 (固定幅) | 値列」 の 2 カラム row レイアウト。 */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 16px;
  }
  .readonly-cell {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    font-size: 0.875rem;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .readonly-label {
    color: #6b7280;
    font-weight: 700;
    font-size: 0.75rem;
  }
  .readonly-value {
    color: #111827;
    word-break: break-word;
  }
  /* InlineField / InlineMultiSelectField を grid 内で table 風 row 配置に上書き。
     :global を使うのは scoped style から子コンポーネントの内部 class を狙うため。 */
  .grid :global(.inline-field) {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    font-size: 0.875rem;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .grid :global(.inline-field > .field-label) {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 700;
  }
  .readonly-value a {
    color: #2563eb;
    text-decoration: none;
  }
  .readonly-value a:hover {
    text-decoration: underline;
  }
  .small {
    font-size: 0.75rem;
  }
  .kv {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 8px 16px;
    margin: 0;
    font-size: 0.875rem;
  }
  .kv > div {
    display: contents;
  }
  .kv dt {
    color: #6b7280;
    font-weight: 700;
    font-size: 0.75rem;
    padding: 4px 0;
  }
  .kv dd {
    margin: 0;
    padding: 4px 0;
    color: #111827;
  }
  .muted {
    color: #6b7280;
  }
  .representative-tags {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    vertical-align: baseline;
  }
  .representative-tag {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    padding: 1px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    background: #f9fafb;
  }
  .representative-edit-btn {
    margin-left: 8px;
    padding: 1px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    font: inherit;
    font-size: 0.75rem;
    color: #374151;
    cursor: pointer;
  }
  .representative-edit-btn:hover {
    background: #f9fafb;
  }
  .exclusion-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
  .exclusion-toggle input[type="checkbox"] {
    margin: 0;
  }
  .checkbox-label {
    font-weight: 700;
    color: #111827;
  }
  .exclusion-active {
    border-color: #fecaca;
    background: #fffbfb;
  }
  .legacy-reason {
    color: #4b5563;
    white-space: pre-wrap;
  }
  /* 住所 (連絡先) 行。 .readonly-cell と同じ 2 カラム row レイアウトに揃える。 */
  .address-field {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    font-size: 0.875rem;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .address-label {
    color: #6b7280;
    font-weight: 700;
    font-size: 0.75rem;
  }
  .address-value {
    color: #111827;
    word-break: break-word;
    min-width: 0;
  }
  /* 表示モードのクリック領域。 InlineField の .inline-display に合わせる。 */
  .address-display {
    display: inline-block;
    text-align: left;
    background: transparent;
    /* text-like の affordance に揃え、 常時うっすら入力枠を出す。 */
    border: 1px dashed var(--border, #e3e6eb);
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: 4px;
    cursor: text;
    font: inherit;
    color: inherit;
    width: 100%;
    min-height: 24px;
  }
  .address-placeholder {
    color: var(--muted, #9ca3af);
  }
  .address-display:hover,
  .address-display:focus-visible {
    border-color: var(--border, #e3e6eb);
    background: #fafbfd;
    outline: none;
  }
  .address-edit {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .address-input {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .address-input-label {
    font-size: 0.7rem;
    color: #6b7280;
  }
  .address-input input,
  .address-input select {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    padding: 4px 6px;
    border: 1px solid var(--primary, #2563eb);
    border-radius: 4px;
    background: #fff;
  }
  .address-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
  }
  .addr-btn {
    padding: 2px 10px;
    border: 1px solid #d1d5db;
    background: #fff;
    border-radius: 4px;
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
  }
  .addr-btn:hover:not(:disabled) {
    background: #f9fafb;
  }
  .addr-save {
    border-color: #2563eb;
    color: #2563eb;
  }
  .addr-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .error-msg {
    color: var(--danger, #dc2626);
  }
</style>
